import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, Package, Edit2, Trash2, Speaker, UtensilsCrossed,
  Sofa, Lightbulb, Tent, Image as ImageIcon, Briefcase, Box, Clock, Tag, Truck, X
} from 'lucide-react';

// Catégories avec icônes par défaut
const CATEGORIES = [
  { value: 'audio', label: 'Audio', icon: Speaker },
  { value: 'lighting', label: 'Éclairage', icon: Lightbulb },
  { value: 'video', label: 'Vidéo', icon: Package },
  { value: 'structure', label: 'Structure', icon: Package },
  { value: 'furniture', label: 'Mobilier', icon: Sofa },
  { value: 'catering', label: 'Restauration', icon: UtensilsCrossed },
  { value: 'tent', label: 'Tente / Chapiteau', icon: Tent },
  { value: 'service', label: 'Prestation', icon: Briefcase },
  { value: 'transport', label: 'Transport', icon: Truck },
  { value: 'other', label: 'Autre', icon: Package },
];

// --- CARTE PRODUIT ---
const ProductCard = ({ product, onEdit, onDelete }) => {
  const categoryDef = CATEGORIES.find(c => c.value === product.category) || CATEGORIES[9];
  const Icon = categoryDef.icon;
  const attributes = product.attributes || {};
  
  // URL de l'image : soit celle uploadée (backend), soit un placeholder
  const imageUrl = product.image_url 
    ? (product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${product.image_url}`) 
    : null;

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden border-muted">
      {/* Zone Image */}
      <div className="h-40 bg-muted/30 relative flex items-center justify-center overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt={product.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
        ) : (
          <div className="text-muted-foreground/30 flex flex-col items-center">
            <Icon className="w-12 h-12 mb-2" />
            <span className="text-xs font-medium uppercase tracking-widest">{categoryDef.label}</span>
          </div>
        )}
        
        {/* Badges sur l'image */}
        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
            <Badge className={product.type === 'service' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-blue-500 hover:bg-blue-600'}>
                {product.type === 'service' ? 'Service' : 'Produit'}
            </Badge>
        </div>
      </div>

      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-2 mb-2">
            <div className="min-w-0">
                <h3 className="font-bold text-lg leading-tight truncate" title={product.name}>{product.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{product.description || 'Aucune description'}</p>
            </div>
            <p className="font-heading font-bold text-lg whitespace-nowrap">{formatCurrency(product.price_ht)}</p>
        </div>

        {/* Affichage des attributs (3 premiers) */}
        {Object.keys(attributes).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
                {Object.entries(attributes).slice(0, 3).map(([key, value]) => (
                    <Badge key={key} variant="secondary" className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground border-none">
                        {key}: {String(value)}
                    </Badge>
                ))}
                {Object.keys(attributes).length > 3 && (
                    <span className="text-[10px] text-muted-foreground self-center">+{Object.keys(attributes).length - 3}</span>
                )}
            </div>
        )}

        <div className="flex items-center justify-between pt-3 mt-2 border-t border-dashed">
             {/* Info spécifique selon le type */}
             <div className="flex items-center gap-2 text-sm">
                {product.type === 'service' ? (
                    <span className="flex items-center gap-1.5 text-purple-600 bg-purple-50 px-2 py-1 rounded-md font-medium">
                        <Clock className="w-4 h-4" /> {product.duration_minutes} min
                    </span>
                ) : (
                    <span className={`flex items-center gap-1.5 px-2 py-1 rounded-md font-medium ${product.stock_quantity > 0 ? 'text-blue-600 bg-blue-50' : 'text-red-600 bg-red-50'}`}>
                        <Tag className="w-4 h-4" /> Stock: {product.stock_quantity}
                    </span>
                )}
             </div>

             {/* Actions */}
             <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(product)}><Edit2 className="w-4 h-4"/></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(product.id)}><Trash2 className="w-4 h-4"/></Button>
             </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============= COMPOSANT ÉDITEUR D'ATTRIBUTS =============
const AttributesEditor = ({ attributes, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const addAttribute = () => {
    if (newKey.trim()) {
      onChange({ ...attributes, [newKey.trim()]: newValue.trim() });
      setNewKey('');
      setNewValue('');
    }
  };

  const removeAttribute = (key) => {
    const newAttributes = { ...attributes };
    delete newAttributes[key];
    onChange(newAttributes);
  };

  return (
    <div className="space-y-3 bg-muted/20 p-3 rounded-lg border">
      <div className="flex items-center justify-between">
          <Label>Caractéristiques techniques</Label>
          <span className="text-xs text-muted-foreground">Ex: Poids, Couleur, Puissance...</span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {Object.entries(attributes).map(([key, value]) => (
          <Badge key={key} variant="secondary" className="gap-1 py-1 px-2 bg-white border">
            <span className="font-medium">{key}:</span> {String(value)}
            <button type="button" onClick={() => removeAttribute(key)} className="ml-1 text-muted-foreground hover:text-destructive">
                <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        {Object.keys(attributes).length === 0 && <span className="text-xs text-muted-foreground italic">Aucune caractéristique</span>}
      </div>

      <div className="flex gap-2">
        <Input 
            placeholder="Nom (ex: Couleur)" 
            value={newKey} 
            onChange={(e) => setNewKey(e.target.value)} 
            className="flex-1 h-8 text-sm"
        />
        <Input 
            placeholder="Valeur (ex: Rouge)" 
            value={newValue} 
            onChange={(e) => setNewValue(e.target.value)} 
            className="flex-1 h-8 text-sm"
            onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addAttribute(); }}}
        />
        <Button type="button" size="sm" variant="outline" onClick={addAttribute} disabled={!newKey.trim()} className="h-8">
            <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// --- FORMULAIRE ---
const ProductFormDialog = ({ product, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', type: 'goods', category: 'other', price_ht: '', stock_quantity: '', duration_minutes: 60, description: '', unit: 'unité', attributes: {}
  });
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setImageFile(null); // Reset file
      if (product) {
        setFormData({
          name: product.name,
          type: product.type || 'goods',
          category: product.category || 'other',
          price_ht: product.price_ht,
          stock_quantity: product.stock_quantity,
          duration_minutes: product.duration_minutes || 60,
          description: product.description || '',
          unit: product.unit || 'unité',
          attributes: product.attributes || {}
        });
      } else {
        setFormData({ name: '', type: 'goods', category: 'other', price_ht: '', stock_quantity: '', duration_minutes: 60, description: '', unit: 'unité', attributes: {} });
      }
    }
  }, [product, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price_ht: parseFloat(formData.price_ht),
      stock_quantity: formData.type === 'goods' ? (parseInt(formData.stock_quantity) || 0) : 0,
      duration_minutes: formData.type === 'service' ? (parseInt(formData.duration_minutes) || 0) : 0,
    }, imageFile);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? 'Modifier' : 'Nouveau'} Élément</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* TYPE SWITCHER */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg">
            <button type="button" onClick={() => setFormData({...formData, type: 'goods'})} className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${formData.type === 'goods' ? 'bg-white shadow text-blue-600' : 'text-muted-foreground hover:text-foreground'}`}>
                <Box className="w-4 h-4" /> Produit / Matériel
            </button>
            <button type="button" onClick={() => setFormData({...formData, type: 'service'})} className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${formData.type === 'service' ? 'bg-white shadow text-purple-600' : 'text-muted-foreground hover:text-foreground'}`}>
                <Briefcase className="w-4 h-4" /> Service / Prestation
            </button>
          </div>

          {/* IMAGE UPLOAD */}
          <div className="flex items-center gap-4 border-2 border-dashed border-muted p-4 rounded-lg hover:border-primary/50 transition-colors cursor-pointer" onClick={() => fileInputRef.current.click()}>
    <div className="w-16 h-16 bg-muted rounded flex items-center justify-center overflow-hidden shrink-0">
        {imageFile ? (
            <img src={URL.createObjectURL(imageFile)} alt="Preview" className="w-full h-full object-cover" />
        ) : product?.image_url ? (
            <img src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${product.image_url}`} 
                alt="Current" 
                className="w-full h-full object-cover" 
            />
        ) : (
            <ImageIcon className="w-8 h-8 text-muted-foreground" />
        )}
    </div>
    <div className="flex-1">
        <p className="text-sm font-medium">Image de l'élément</p>
        <p className="text-xs text-muted-foreground">{imageFile ? imageFile.name : "Cliquez pour changer l'image (Max 2MB)"}</p>
    </div>
    <input 
    type="file" 
    ref={fileInputRef} 
    className="hidden" 
    accept="image/*" 
    onChange={(e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            
            // 1. Validation du type
            if (!file.type.startsWith('image/')) {
                toast.error('Le fichier sélectionné n\'est pas une image.');
                return;
            }
            
            // 2. Validation de la taille (2 MB = 2 * 1024 * 1024 octets)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('L\'image est trop volumineuse (Maximum 2 MB).');
                return;
            }
            
            // Si tout est bon, on met à jour le state
            setImageFile(file);
        }
    }} 
/>
</div>

          {/* CHAMPS COMMUNS */}
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder={formData.type === 'goods' ? "Ex: Micro Shure SM58" : "Ex: Installation Technique"} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prix HT (€)</Label>
              <Input type="number" required step="0.01" value={formData.price_ht} onChange={e => setFormData({...formData, price_ht: e.target.value})} />
            </div>
          </div>

          {/* CHAMPS SPÉCIFIQUES */}
          <div className="grid grid-cols-2 gap-4 bg-muted/20 p-3 rounded-lg border">
            {formData.type === 'goods' ? (
                <>
                    <div className="space-y-2">
                        <Label>Stock Initial</Label>
                        <Input type="number" value={formData.stock_quantity} onChange={e => setFormData({...formData, stock_quantity: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                        <Label>Unité</Label>
                        <Input value={formData.unit} onChange={e => setFormData({...formData, unit: e.target.value})} placeholder="pièce" />
                    </div>
                </>
            ) : (
                <div className="space-y-2 col-span-2">
                    <Label>Durée (minutes)</Label>
                    <div className="relative">
                        <Clock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input type="number" className="pl-9" value={formData.duration_minutes} onChange={e => setFormData({...formData, duration_minutes: e.target.value})} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Sera utilisé pour calculer la durée dans l'agenda.</p>
                </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Détails techniques..." />
          </div>

          {/* ÉDITEUR D'ATTRIBUTS RÉINTÉGRÉ */}
          <AttributesEditor 
             attributes={formData.attributes || {}} 
             onChange={(attrs) => setFormData({ ...formData, attributes: attrs })} 
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit">Enregistrer</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============= MAIN PAGE =============
const ProductsPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  
  // FILTRES
  const [typeFilter, setTypeFilter] = useState('all'); // all, goods, service
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products', typeFilter, categoryFilter],
    queryFn: async () => {
      // Construction query params
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const res = await fetch(`${API_URL}/products?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({ data, image }) => {
      // 1. Créer le produit
      const res = await fetch(`${API_URL}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create');
      const product = await res.json();
      
      // 2. Upload image si présente
      if (image) {
          const formData = new FormData();
          formData.append('file', image);
          await fetch(`${API_URL}/products/${product.id}/image`, {
             method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
          });
      }
      return product;
    },
    onSuccess: () => { queryClient.invalidateQueries(['products']); setIsDialogOpen(false); toast.success('Élément créé'); },
    onError: () => toast.error('Erreur création'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data, image }) => {
      await fetch(`${API_URL}/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      
      if (image) {
          const formData = new FormData();
          formData.append('file', image);
          await fetch(`${API_URL}/products/${id}/image`, {
             method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
          });
      }
    },
    onSuccess: () => { queryClient.invalidateQueries(['products']); setIsDialogOpen(false); setEditingProduct(null); toast.success('Mis à jour'); },
    onError: () => toast.error('Erreur mise à jour'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await fetch(`${API_URL}/products/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => { queryClient.invalidateQueries(['products']); toast.success('Supprimé'); }
  });

  const handleSave = (data, image) => {
    if (editingProduct) updateMutation.mutate({ id: editingProduct.id, data, image });
    else createMutation.mutate({ data, image });
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading">Catalogue</h1>
          <p className="text-muted-foreground">Gérez votre matériel et vos prestations</p>
        </div>
        <Button onClick={() => { setEditingProduct(null); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter un élément
        </Button>
      </div>

      {/* FILTRES COMPLETS */}
      <div className="flex flex-col lg:flex-row gap-4 bg-muted/20 p-3 rounded-xl border">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
          </div>
          
          <div className="flex gap-2 w-full lg:w-auto">
             {/* Filtre TYPE */}
             <div className="flex bg-white dark:bg-slate-950 rounded-lg border p-1 shrink-0">
                <button onClick={() => setTypeFilter('all')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${typeFilter === 'all' ? 'bg-slate-100 dark:bg-slate-800 text-foreground' : 'text-muted-foreground hover:bg-muted'}`}>Tout</button>
                <button onClick={() => setTypeFilter('goods')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${typeFilter === 'goods' ? 'bg-blue-100 text-blue-700' : 'text-muted-foreground hover:bg-muted'}`}>Produits</button>
                <button onClick={() => setTypeFilter('service')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${typeFilter === 'service' ? 'bg-purple-100 text-purple-700' : 'text-muted-foreground hover:bg-muted'}`}>Services</button>
             </div>

             {/* Filtre CATEGORY */}
             <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px] bg-white dark:bg-slate-950"><SelectValue placeholder="Catégorie" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
             </Select>
          </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           {[1,2,3].map(i => <Skeleton key={i} className="h-60 w-full" />)}
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((p) => (
            <ProductCard 
                key={p.id} 
                product={p} 
                onEdit={setEditingProduct} 
                onDelete={(id) => { if(window.confirm('Supprimer ?')) deleteMutation.mutate(id); }} 
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-muted/20 rounded-xl border-dashed border-2">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-muted-foreground opacity-50"/>
            </div>
            <h3 className="font-bold text-lg">Catalogue vide</h3>
            <p className="text-muted-foreground">Aucun élément ne correspond à vos filtres.</p>
        </div>
      )}

      <ProductFormDialog 
        product={editingProduct} 
        open={isDialogOpen || !!editingProduct} 
        onOpenChange={(o) => { setIsDialogOpen(o); if(!o) setEditingProduct(null); }} 
        onSave={handleSave} 
        categories={CATEGORIES} 
      />
    </div>
  );
};

export default ProductsPage;